import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { type ITabItem, Tabs, Button } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { PresetContainer, PresetFooter } from 'shared/Preset'
import { ChatbotInstructionType } from 'entities/Chatbot'
import { type IAICallingType } from 'entities/AICalling/api/types'
import { type CreateChatbotStore } from 'pages/chatbot/store/chatbotCreateStore'
import { type IAgentType } from 'pages/chatbot/store/type'
import { ChatbotPath } from 'pages/chatbot/route/chatbotPath'
import { ChatbotRoutes } from 'pages/chatbot'
import { TextingContent } from 'pages/chatbot/ui/ChatbotCreate/TextingContent'
import { CallingContent } from 'pages/chatbot/ui/ChatbotCreate/CallingContent'
import styles from './styles.module.scss'

export const ChatbotTemplateModal = observer(({ store }: { store: CreateChatbotStore }) => {
  const { instructions, chatbot, onClose } = store
  const [activeTabKey, setActiveTabKey] = useState<IAgentType>(store.type || 'texting')
  const [selectedTextingType, setSelectedTextingType] = useState<ChatbotInstructionType>(
    ChatbotInstructionType.Custom
  )
  const [selectedCallingType, setSelectedCallingType] = useState<IAICallingType>('custom')

  const tabs: ITabItem<IAgentType>[] = [
    {
      key: 'texting',
      name: 'Texting',
    },
    {
      key: 'calling',
      name: 'Calling',
      statusProps: {
        intent: 'purple',
        emphasis: 'high',
        title: 'New',
        size: 'label',
      },
    },
  ]

  const handleSelectTab = (tab: ITabItem<IAgentType>) => {
    setActiveTabKey(tab.key)
    setSelectedTextingType(ChatbotInstructionType.Custom)
    setSelectedCallingType('custom')
  }

  const handleTextingNext = () => {
    const instruction = instructions.get(selectedTextingType)
    if (instruction) chatbot.settings.setInstruction(instruction)
    chatbot.setType(selectedTextingType)

    onClose()

    const payload = chatbot.toJSON()
    if (!payload) return

    const path =
      payload.type === ChatbotInstructionType.BookAppointment
        ? `/${ChatbotPath.newBookAppointment}`
        : `/${ChatbotRoutes.root}/${ChatbotRoutes.new}`

    uiStore.changeRoute({
      path,
      options: { state: payload },
    })
  }

  const handleCallingNext = () => {
    onClose()
    uiStore.changeRoute({ path: ChatbotPath.callingNew(selectedCallingType) })
  }

  const handleNext = () => {
    if (activeTabKey === 'texting') {
      handleTextingNext()
    } else {
      handleCallingNext()
    }
  }

  return (
    <PresetContainer>
      <Tabs
        tabs={tabs}
        activeTabKey={activeTabKey}
        handleSelectTab={handleSelectTab}
        marginInlineTab={18}
      />
      {activeTabKey === 'texting' && (
        <TextingContent selectedType={selectedTextingType} onSelectType={setSelectedTextingType} />
      )}
      {activeTabKey === 'calling' && (
        <CallingContent selectedType={selectedCallingType} onSelectType={setSelectedCallingType} />
      )}
      <PresetFooter className={styles.footer}>
        <Button contained='secondary' size='medium' text='Cancel' onClick={onClose} />
        <Button typeBtn='contained' size='medium' text='Next' onClick={handleNext} />
      </PresetFooter>
    </PresetContainer>
  )
})
